// 先代の姫「自身の技」8案の影絵（2026-11-05）。
//   作者（11-05）「先代固有の技(ルネール強化版のような)」「1.混ぜる」（前半の借りる技の合間にも混ぜる）。
//   先代は先代のルネール。主人公の今の技（連撃3段・3段目の跳び突き・パリィ・低いステップ・見切り反撃・致命の刺して抜く）を
//   一段上にした物を並べる。灯りの輪は出さない（輪が出ない技＝先代自身の技、と読ませるため）。指の絵・指を折る仕草も出さない。
//   体は主人公と同じ作り（脚は腰・膝・足首の3つ・細剣）。柄の輪は剣を持つ手に小さく描く（灯りの輪とは別の金具）。
//   拍はどれも仮。主人公（白い棒）は受ける・飛ばされる・振って弾かれる役。

import { bakeTracks, poseAt, type MockupMove, type MockupPhase, type StageEnv, type TrackKey, type Tracks, type Vec } from '../core'
import { humanPose } from '../body'

type Ctx = CanvasRenderingContext2D
const clamp01 = (v: number) => Math.max(0, Math.min(1, v))

// ---------- 先代の体 ----------
const STAND = { px: 0, py: -70, lean: 6, tw: 0, ht: 0, aF: 40, bF: 10, sF: 4, aB: 120, bB: 68, sB: 0, fF: [12, 0] as Vec, fB: [-10, 0] as Vec, rF: 0, rB: 0 }
type Rig = typeof STAND
type P = Partial<Rig>
const build = (v: Record<string, number | Vec>) => humanPose({ ...STAND, ...v } as unknown as Parameters<typeof humanPose>[0])

/** 構え（突く前に剣を引く）・突き・深い踏み込み。px は体の位置 */
const CH = (px: number): P => ({ px, py: -66, lean: 0, tw: -24, aF: 120, bF: 6, sF: 0, aB: 130, bB: 90 })
const JAB = (px: number): P => ({ px, py: -63, lean: 16, tw: 24, aF: 2, bF: -4, sF: 2, aB: 140, bB: 120 })
const LUNGE = (px: number): P => ({ px, py: -55, lean: 26, tw: 30, aF: -6, bF: -10, sF: -2, aB: 150, bB: 140, rB: 0.6 })
const STAY = (px: number): P => ({ ...STAND, px, fF: [px + 12, 0], fB: [px - 10, 0] })

type Key = [at: number, v: P, ease?: TrackKey[2]]
/**
 * 姿勢のキーの並び → 時間の線。書かなかった値は前のキーのまま。
 * 足は床の位置（[x, y]）。床の上で 3 より動く時は、間に浮いた点を足して1歩に見せる（真上へ上げて運び、真上から下ろす）。
 */
function seq(keys: Key[]): Tracks {
  const names = Object.keys(STAND) as (keyof Rig)[]
  const out: Tracks = {}
  for (const n of names) {
    const tr: TrackKey[] = []
    let prev: number | Vec = STAND[n], prevAt = 0
    keys.forEach(([at, v, ease], i) => {
      const cur = (v[n] ?? prev) as number | Vec
      if (Array.isArray(cur) && Array.isArray(prev) && i > 0) {
        const [x0, y0] = prev, [x1, y1] = cur, d = at - prevAt
        if (y0 === 0 && y1 === 0 && Math.abs(x1 - x0) > 3 && d > 40) {
          const h = Math.min(20, 8 + Math.abs(x1 - x0) * 0.3), dx = x1 - x0
          tr.push([prevAt + d * 0.3, [x0 + dx * 0.15, -h], 'io'], [at - d * 0.3, [x1 - dx * 0.15, -h * 0.7], 'io'])
        }
      }
      tr.push([at, cur, i ? (ease ?? 'io') : undefined])
      prev = cur; prevAt = at
    })
    out[n] = tr
  }
  return out
}

// ---------- 主人公（白い棒）----------
type HeroPose = 'stand' | 'guard' | 'swing' | 'raise' | 'hit'
type HeroKey = [at: number, x: number, pose: HeroPose, lean?: number, lift?: number]
function heroAt(keys: HeroKey[], t: number): { x: number; pose: HeroPose; lean: number; lift: number } {
  let i = 0
  while (i < keys.length - 1 && keys[i + 1]![0] <= t) i++
  const a = keys[i]!, b = keys[i + 1] ?? a
  const u = b[0] === a[0] ? 0 : clamp01((t - a[0]) / (b[0] - a[0])), e = u * u * (3 - 2 * u)
  const L = (p: number | undefined, q: number | undefined) => (p ?? 0) + ((q ?? 0) - (p ?? 0)) * e
  return { x: a[1] + (b[1] - a[1]) * e, pose: a[2], lean: L(a[3], b[3]), lift: L(a[4], b[4]) }
}
/** 剣の向き（度・0＝右＝先代の方・負＝上） */
const HERO_SWORD: Record<HeroPose, [Vec, number]> = {
  stand: [[12, 22], -40], guard: [[14, 6], -80], swing: [[18, 20], 10], raise: [[8, -6], -150], hit: [[6, 30], -120],
}
function drawHero(ctx: Ctx, h: { x: number; pose: HeroPose; lean: number; lift: number }, e: StageEnv): void {
  const { k, G } = e, s = 0.62 * k, x = h.x * k, g = G - h.lift * k, a = h.lean * Math.PI / 180
  const up = (dy: number): Vec => [x + dy * Math.sin(a), g - 52 * s - dy * Math.cos(a)]
  ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 1.8 * k; ctx.lineCap = 'round'; ctx.globalAlpha = 0.9
  const seg = (p: Vec, q: Vec) => { ctx.beginPath(); ctx.moveTo(p[0], p[1]); ctx.lineTo(q[0], q[1]); ctx.stroke() }
  const hip: Vec = [x, g - 52 * s], head = up(56 * s), sh = up(36 * s)
  ctx.beginPath(); ctx.arc(head[0], head[1], 12.6 * s, 0, Math.PI * 2); ctx.stroke()
  seg(hip, up(44 * s)); seg(hip, [x - 6 * k, g]); seg(hip, [x + 6 * k, g])
  const [[ax, ay], sw] = HERO_SWORD[h.pose]
  const hand: Vec = [sh[0] + ax * s, sh[1] + (ay - 36) * s * 0.6]
  seg(sh, hand); seg(sh, [sh[0] - 8 * s, sh[1] + 22 * s])
  const sa = (sw + h.lean) * Math.PI / 180
  ctx.strokeStyle = '#d8dde6'; ctx.lineWidth = 1.4 * k
  seg(hand, [hand[0] + Math.cos(sa) * 34 * s, hand[1] + Math.sin(sa) * 34 * s])
  ctx.globalAlpha = 1
}

// ---------- 8案 ----------
interface Own {
  id: string
  no: string
  name: string
  /** 元にした主人公の技 → 一段上にした所 */
  from: string
  up: string
  dur: number
  keys: Key[]
  hero: HeroKey[]
  hits: number[]
  /** 弾いた瞬間（白い火花） */
  parries?: number[]
  phases: MockupPhase[]
}

const ph = (from: number, to: number, label: string, kind: MockupPhase['kind']): MockupPhase => ({ from, to, label, kind })

// ① 五連の突き：連撃の3段 → 5段。1打ごとの間を詰め、2打ごとに前へ出る
const FIVE_HITS = [560, 790, 1020, 1250, 1560]
const five: Own = {
  id: 'princess-own-five', no: '①', name: '五連の突き', from: '連撃（3段）', up: '5段・間を詰めて前へ出続ける', dur: 2900,
  keys: [
    [0, STAY(0)], [380, { ...CH(0), fF: [12, 0], fB: [-12, 0] }],
    [FIVE_HITS[0]!, { ...JAB(8), fF: [26, 0] }, 'in'], [FIVE_HITS[0]! + 120, { ...CH(8), aF: 100, tw: -12 }],
    [FIVE_HITS[1]!, { ...JAB(14), fB: [-2, 0] }, 'in'], [FIVE_HITS[1]! + 120, { ...CH(16), aF: 100, tw: -12, fF: [36, 0] }],
    [FIVE_HITS[2]!, JAB(22), 'in'], [FIVE_HITS[2]! + 120, { ...CH(24), aF: 100, tw: -12, fB: [8, 0] }],
    [FIVE_HITS[3]!, { ...JAB(30), fF: [46, 0] }, 'in'], [FIVE_HITS[3]! + 160, { ...CH(26), py: -62, aF: 140, tw: -30, fB: [6, 0] }],
    [FIVE_HITS[4]!, { ...LUNGE(52), fF: [86, 0] }, 'in'], [FIVE_HITS[4]! + 380, LUNGE(52)],
    [FIVE_HITS[4]! + 800, { ...STAY(52), fF: [86, 0], fB: [6, 0] }], [FIVE_HITS[4]! + 1050, { fF: [64, 0] }], [2800, { fB: [42, 0] }],
  ],
  hero: [[0, 178, 'guard'], [FIVE_HITS[0]!, 178, 'guard', -4], [FIVE_HITS[1]!, 172, 'guard', -6], [FIVE_HITS[2]!, 166, 'guard', -6],
    [FIVE_HITS[3]!, 160, 'guard', -8], [FIVE_HITS[4]! - 10, 156, 'guard', -10], [FIVE_HITS[4]!, 156, 'hit', -8], [FIVE_HITS[4]! + 260, 124, 'hit', -24], [2900, 124, 'hit', -18]],
  hits: FIVE_HITS, phases: [ph(0, 380, '構え', 'rest'), ph(380, FIVE_HITS[0]!, '引く', 'tell'), ph(FIVE_HITS[0]!, FIVE_HITS[3]! + 60, '4打（受けさせて押す）', 'hit'),
    ph(FIVE_HITS[3]! + 60, FIVE_HITS[4]!, '溜め（短い）', 'tell'), ph(FIVE_HITS[4]!, FIVE_HITS[4]! + 380, '5打目の踏み込み', 'hit'), ph(FIVE_HITS[4]! + 380, 2900, '戻り', 'rest')],
}

// ② 遠間の跳び突き：3段目の後ろステップ→跳び突き → 二歩下がって遠くから、高く長く跳ぶ
const FAR_HIT = 1480
const far: Own = {
  id: 'princess-own-far', no: '②', name: '遠間の跳び突き', from: '3段目の跳び突き', up: '二歩下がり、ずっと遠くから跳ぶ', dur: 3000,
  keys: [
    [0, STAY(0)], [320, { px: -10, lean: 0, fB: [-26, 0] }], [520, { px: -22, fF: [-8, 0] }], [720, { px: -34, fB: [-48, 0] }],
    [880, { px: -38, py: -60, lean: -2, aF: 130, bF: 4, sF: -4, tw: -26, fF: [-20, 0] }],
    [1080, { ...CH(-40), py: -56, lean: 6, fF: [-20, 0], fB: [-52, 0], rB: 0.5 }],
    [1140, { px: -30, py: -74, lean: 14, fF: [-14, -8], fB: [-50, -4], rF: 0.6, rB: 0.9 }, 'out'],
    [1300, { px: 14, py: -104, lean: 20, aF: 60, bF: 0, fF: [26, -34], fB: [-14, -40] }, 'lin'],
    [FAR_HIT, { ...LUNGE(64), fF: [100, 0], fB: [16, 0], rF: 0 }, 'in'], [FAR_HIT + 380, LUNGE(64)],
    [FAR_HIT + 820, { ...STAY(64), fF: [100, 0], fB: [16, 0] }], [FAR_HIT + 1060, { fF: [76, 0] }], [2950, { fB: [54, 0] }],
  ],
  hero: [[0, 128, 'stand'], [FAR_HIT - 120, 128, 'stand'], [FAR_HIT - 60, 128, 'guard', -4], [FAR_HIT, 126, 'hit', -6], [FAR_HIT + 300, 96, 'hit', -26, 0], [3000, 96, 'hit', -20]],
  hits: [FAR_HIT], phases: [ph(0, 320, '構え', 'rest'), ph(320, 880, '二歩下がる', 'tell'), ph(880, 1140, '沈む', 'tell'), ph(1140, FAR_HIT, '長く跳ぶ', 'tell'),
    ph(FAR_HIT, FAR_HIT + 380, '突き', 'hit'), ph(FAR_HIT + 380, 3000, '戻り', 'rest')],
}

// ③ 二度跳び：跳び突き → 着いたその場からもう一度跳ぶ
const TWICE = [880, 1420]
const twice: Own = {
  id: 'princess-own-twice', no: '③', name: '二度跳び', from: '3段目の跳び突き', up: '着いた所から間を置かずもう一度跳ぶ', dur: 2900,
  keys: [
    [0, STAY(0)], [300, { px: -8, lean: 0, fB: [-22, 0] }], [520, { ...CH(-12), py: -58, fF: [2, 0], fB: [-24, 0], rB: 0.5 }],
    [600, { px: -4, py: -78, lean: 12, fF: [8, -10], fB: [-22, -6], rF: 0.6, rB: 0.9 }, 'out'],
    [740, { px: 6, py: -96, lean: 18, aF: 70, bF: 0, fF: [20, -26], fB: [-10, -30] }, 'lin'],
    [TWICE[0]!, { ...LUNGE(22), py: -58, fF: [54, 0], fB: [-14, 0], rF: 0 }, 'in'], [TWICE[0]! + 120, LUNGE(22)],
    // 戻らずに沈んで、もう一度
    [TWICE[0]! + 280, { ...CH(20), py: -56, lean: 8, fF: [54, 0], fB: [-14, 0], rB: 0.6 }],
    [TWICE[0]! + 340, { px: 28, py: -78, lean: 14, fF: [58, -8], fB: [-8, -10], rF: 0.6, rB: 0.9 }, 'out'],
    [TWICE[0]! + 440, { px: 38, py: -96, lean: 20, aF: 70, bF: 0, fF: [64, -22], fB: [8, -30] }, 'lin'],
    [TWICE[1]!, { ...LUNGE(52), fF: [86, 0], fB: [10, 0], rF: 0 }, 'in'], [TWICE[1]! + 380, LUNGE(52)],
    [TWICE[1]! + 800, { ...STAY(52), fF: [86, 0], fB: [10, 0] }], [TWICE[1]! + 1040, { fF: [64, 0] }], [2850, { fB: [42, 0] }],
  ],
  hero: [[0, 172, 'stand'], [TWICE[0]! - 100, 172, 'guard'], [TWICE[0]!, 170, 'guard', -6], [TWICE[0]! + 200, 152, 'guard', -10], [TWICE[1]! - 20, 150, 'guard', -6],
    [TWICE[1]!, 150, 'hit', -8], [TWICE[1]! + 280, 118, 'hit', -26], [2900, 118, 'hit', -20]],
  hits: TWICE, phases: [ph(0, 300, '構え', 'rest'), ph(300, 600, '下がって沈む', 'tell'), ph(600, TWICE[0]!, '跳ぶ', 'tell'), ph(TWICE[0]!, TWICE[0]! + 120, '1つ目（受けさせる）', 'hit'),
    ph(TWICE[0]! + 120, TWICE[1]!, 'すぐに2つ目へ跳ぶ', 'tell'), ph(TWICE[1]!, TWICE[1]! + 380, '2つ目', 'hit'), ph(TWICE[1]! + 380, 2900, '戻り', 'rest')],
}

// ④ 這う突き上げ：見切り反撃（低い姿勢から伸び上がって突く）→ 自分から低く滑り込み、懐の下から突き上げる
const UP_HIT = 1180
const under: Own = {
  id: 'princess-own-under', no: '④', name: '這う突き上げ', from: '見切り反撃', up: '自分から低く滑り込み、懐の下から突き上げる', dur: 3000,
  keys: [
    [0, STAY(0)], [380, { px: 4, py: -56, lean: 30, tw: -10, aF: 110, bF: 20, sF: 20, aB: 160, bB: 170, fF: [30, 0], fB: [-14, 0] }],
    [560, { px: 30, py: -40, lean: 52, aF: 130, bF: 40, sF: 30, fF: [70, 0], fB: [-20, 0], rB: 0.7 }],
    [880, { px: 66, py: -38, lean: 50, fF: [70, 0], fB: [10, 0], rB: 0.8 }, 'out'],
    [960, { px: 70, py: -44, lean: 40, tw: -24, aF: 120, bF: 60, sF: 40, fF: [92, 0] }],
    [UP_HIT, { px: 78, py: -80, lean: -6, tw: 30, aF: -70, bF: -66, sF: -62, aB: 150, bB: 160, fF: [92, 0], fB: [40, 0], rB: 0.9 }, 'in'],
    [UP_HIT + 380, { px: 78, py: -80, lean: -6, aF: -70, bF: -66, sF: -62 }],
    [UP_HIT + 820, { ...STAY(70), fF: [92, 0], fB: [40, 0] }], [UP_HIT + 1060, { fF: [82, 0] }], [2950, { fB: [60, 0] }],
  ],
  hero: [[0, 160, 'stand'], [420, 160, 'raise'], [760, 160, 'swing', 8], [UP_HIT - 20, 160, 'swing', 10], [UP_HIT, 160, 'hit', -6, 0],
    [UP_HIT + 220, 136, 'hit', -26, 22], [UP_HIT + 480, 118, 'hit', -30, 0], [3000, 118, 'hit', -24]],
  hits: [UP_HIT], phases: [ph(0, 380, '構え', 'rest'), ph(380, 880, '低く滑り込む（主人公の振りの下）', 'tell'), ph(880, UP_HIT, '伸び上がる', 'tell'),
    ph(UP_HIT, UP_HIT + 380, '突き上げ', 'hit'), ph(UP_HIT + 380, 3000, '戻り', 'rest')],
}

// ⑤ 受けの返し：パリィ → 構えて待ち、主人公の振りを弾いた瞬間に突き返す
const PARRY = 1000, RET = 1160
const ward: Own = {
  id: 'princess-own-ward', no: '⑤', name: '受けの返し', from: 'パリィ', up: '構えて待ち、弾いたその場で突き返す', dur: 2900,
  keys: [
    [0, STAY(0)], [300, { px: 0, py: -66, lean: 2, tw: 10, aF: 30, bF: -40, sF: -76, aB: 120, bB: 68, fF: [16, 0], fB: [-14, 0] }],
    [PARRY - 60, { px: 0, py: -66, aF: 30, bF: -40, sF: -76 }],
    [PARRY, { px: -2, py: -64, lean: -2, tw: 18, aF: 10, bF: -70, sF: -40 }, 'in'],
    [PARRY + 80, { ...CH(-2), py: -62, fF: [16, 0], fB: [-14, 0] }],
    [RET, { ...LUNGE(18), fF: [52, 0], fB: [-14, 0] }, 'in'], [RET + 380, LUNGE(18)],
    [RET + 820, { ...STAY(18), fF: [52, 0], fB: [-14, 0] }], [RET + 1060, { fF: [30, 0] }], [2850, { fB: [8, 0] }],
  ],
  hero: [[0, 172, 'stand'], [500, 172, 'stand'], [760, 186, 'raise', 4], [PARRY - 10, 190, 'swing', 10], [PARRY, 190, 'raise', -10],
    [RET - 10, 184, 'raise', -14], [RET, 184, 'hit', -12], [RET + 300, 150, 'hit', -28], [2900, 150, 'hit', -22]],
  hits: [RET], parries: [PARRY], phases: [ph(0, 300, '構え', 'rest'), ph(300, PARRY, '受けの構えで待つ', 'tell'), ph(PARRY, PARRY + 80, '弾く', 'hit'),
    ph(PARRY + 80, RET, '間を置かず', 'tell'), ph(RET, RET + 380, '突き返す', 'hit'), ph(RET + 380, 2900, '戻り', 'rest')],
}

// ⑥ 立ったままえぐる：致命の「刺してえぐって抜く」→ 掴まずに立った相手へ。長い溜めの大突き
const DEEP = 1380, PULL = 2020
const deep: Own = {
  id: 'princess-own-deep', no: '⑥', name: '立ったままえぐる', from: '致命（刺してえぐって抜く）', up: '崩さずに立った相手へ・長い溜めの大突き', dur: 3200,
  keys: [
    [0, STAY(0)], [400, { px: -6, py: -64, lean: -8, tw: -34, aF: 146, bF: 138, sF: 212, aB: 60, bB: 0, fF: [14, 0], fB: [-30, 0], rB: 0.4 }],
    [1180, { px: -10, py: -62, lean: -10, tw: -38, aF: 150, bF: 142, sF: 216, aB: 50, bB: -6 }],
    [DEEP, { ...LUNGE(34), aB: 160, bB: 150, fF: [74, 0], fB: [-30, 0] }, 'in'],
    [DEEP + 120, { px: 36, tw: 22, sF: -8 }], [DEEP + 260, { px: 36, tw: 36, sF: 6 }], [DEEP + 400, { px: 36, tw: 22, sF: -8 }], [DEEP + 540, { px: 36, tw: 34, sF: 4 }],
    [PULL, { px: 8, py: -66, lean: 0, tw: -20, aF: 110, bF: 20, sF: 0, fF: [52, 0] }, 'out'],
    [PULL + 480, { ...STAY(8), fF: [52, 0], fB: [-30, 0] }], [PULL + 700, { fF: [24, 0] }], [3150, { fB: [-2, 0] }],
  ],
  hero: [[0, 166, 'stand'], [900, 166, 'guard'], [DEEP - 10, 166, 'guard'], [DEEP, 164, 'hit', 10], [PULL, 164, 'hit', 14],
    [PULL + 200, 152, 'hit', -20], [PULL + 520, 140, 'hit', -40], [3200, 140, 'hit', -40]],
  hits: [DEEP], phases: [ph(0, 400, '構え', 'rest'), ph(400, DEEP, '長い溜め（剣を背へ大きく引く）', 'tell'), ph(DEEP, DEEP + 600, '大突き・えぐる', 'hit'),
    ph(DEEP + 600, PULL, '刺したまま', 'hit'), ph(PULL, 3200, '抜く', 'rest')],
}

// ⑦ 揺さぶりの足：低いステップ → 前・後ろ・前と速く刻んで出所を読ませず突く
const FEINT_HIT = 1100
const feint: Own = {
  id: 'princess-own-feint', no: '⑦', name: '揺さぶりの足', from: '低いステップ（回避）', up: '前・後ろ・前と速く刻み、出所を読ませない', dur: 2800,
  keys: [
    [0, STAY(0)], [260, { px: 0, py: -60, lean: 14, tw: -10, aF: 90, bF: 10, sF: 4, aB: 140, bB: 120, fF: [16, 0], fB: [-14, 0] }],
    [440, { px: 24, py: -56, lean: 20, fF: [44, 0], fB: [6, 0] }],
    [640, { px: -4, py: -58, lean: 4, fF: [14, 0], fB: [-22, 0] }],
    [860, { px: 26, py: -56, lean: 18, aF: 120, bF: 6, tw: -20, fF: [46, 0], fB: [6, 0] }],
    [FEINT_HIT, { ...JAB(36), fF: [62, 0], fB: [6, 0] }, 'in'], [FEINT_HIT + 300, JAB(36)],
    [FEINT_HIT + 700, { ...STAY(30), fF: [62, 0], fB: [6, 0] }], [FEINT_HIT + 940, { fF: [42, 0] }], [2750, { fB: [20, 0] }],
  ],
  hero: [[0, 174, 'stand'], [400, 174, 'guard'], [560, 174, 'stand'], [800, 174, 'guard'], [FEINT_HIT - 10, 174, 'stand'], [FEINT_HIT, 174, 'hit', -6],
    [FEINT_HIT + 260, 146, 'hit', -24], [2800, 146, 'hit', -20]],
  hits: [FEINT_HIT], phases: [ph(0, 260, '構え', 'rest'), ph(260, 860, '前・後ろ・前と刻む', 'tell'), ph(860, FEINT_HIT, '構えの外れを突く', 'tell'),
    ph(FEINT_HIT, FEINT_HIT + 300, '突き', 'hit'), ph(FEINT_HIT + 300, 2800, '戻り', 'rest')],
}

// ⑧ 落とし突き：跳び突き（横へ跳ぶ）→ 高く跳び、上から斜め下へ突き下ろす
const DROP = 1240
const drop: Own = {
  id: 'princess-own-drop', no: '⑧', name: '落とし突き', from: '3段目の跳び突き', up: '高く跳び、上から斜め下へ突き下ろす', dur: 3000,
  keys: [
    [0, STAY(0)], [380, { px: -2, py: -54, lean: 10, tw: -10, aF: 140, bF: 120, sF: 100, aB: 130, bB: 90, fF: [14, 0], fB: [-14, 0], rB: 0.4 }],
    [640, { px: -2, py: -52, lean: 14, fF: [14, 0], fB: [-14, 0] }],
    [720, { px: 6, py: -80, lean: 6, fF: [18, -8], fB: [-8, -8], rF: 0.8, rB: 0.8 }, 'out'],
    [980, { px: 30, py: -112, lean: -10, tw: -20, aF: -30, bF: -20, sF: 70, fF: [44, -46], fB: [14, -48], rF: 0.3, rB: 0.3 }, 'out'],
    [DROP, { px: 62, py: -96, lean: 30, tw: 26, aF: 50, bF: 50, sF: 46, aB: 170, bB: 160, fF: [82, -30], fB: [40, -44] }, 'in'],
    [DROP + 160, { px: 70, py: -56, lean: 30, fF: [96, 0], fB: [40, 0], rF: 0, rB: 0.6 }, 'in'], [DROP + 420, { px: 70, py: -56 }],
    [DROP + 860, { ...STAY(70), fF: [96, 0], fB: [40, 0] }], [DROP + 1100, { fF: [82, 0] }], [2950, { fB: [60, 0] }],
  ],
  hero: [[0, 168, 'stand'], [800, 168, 'stand'], [1000, 168, 'guard', -4], [DROP - 10, 168, 'guard', -6], [DROP, 166, 'hit', 18],
    [DROP + 300, 140, 'hit', -20], [3000, 140, 'hit', -24]],
  hits: [DROP], phases: [ph(0, 380, '構え', 'rest'), ph(380, 720, '沈む', 'tell'), ph(720, 980, '高く跳ぶ', 'tell'), ph(980, DROP, '上から落ちる', 'tell'),
    ph(DROP, DROP + 160, '突き下ろす', 'hit'), ph(DROP + 160, 3000, '着いて戻る', 'rest')],
}

export const PRINCESS_OWN_LIST: Own[] = [five, far, twice, under, ward, deep, feint, drop]

// ---------- 舞台：柄の輪・当たり・字幕 ----------
function front(o: Own, m: MockupMove) {
  return (ctx: Ctx, t: number, e: StageEnv): void => {
    const { k, w } = e
    // 柄の輪（剣を持つ手の小さな金具。灯りの輪ではない）
    const p = poseAt(m, t), hF = p.hF as Vec, q = e.at([hF[0] + (p.rx as number), hF[1] + (p.ry as number)])
    ctx.strokeStyle = '#aab2c0'; ctx.lineWidth = 1.2 * k; ctx.globalAlpha = 0.9
    ctx.beginPath(); ctx.arc(q[0], q[1], 3.6 * k, 0, Math.PI * 2); ctx.stroke(); ctx.globalAlpha = 1
    // 弾いた瞬間：白い火花
    for (const at of o.parries ?? []) {
      const d = t - at
      if (d < 0 || d > 180) continue
      const sa = (p.sF as number) * Math.PI / 180, tip = e.at([hF[0] + (p.rx as number) + Math.cos(sa) * 40, hF[1] + Math.sin(sa) * 40]), v = 1 - d / 180
      ctx.strokeStyle = '#ffffff'; ctx.globalAlpha = v; ctx.lineWidth = 1.6 * k
      for (let i = 0; i < 6; i++) {
        const a = i / 6 * Math.PI * 2, r0 = 4 * k, r1 = (8 + 14 * (1 - v)) * k
        ctx.beginPath(); ctx.moveTo(tip[0] + Math.cos(a) * r0, tip[1] + Math.sin(a) * r0); ctx.lineTo(tip[0] + Math.cos(a) * r1, tip[1] + Math.sin(a) * r1); ctx.stroke()
      }
      ctx.globalAlpha = 1
    }
    // 当たった瞬間：主人公に赤を薄く重ねる
    for (const at of o.hits) {
      const d = t - at
      if (d < 0 || d > 160) continue
      const h = heroAt(o.hero, t)
      ctx.globalAlpha = 0.35 * (1 - d / 160); ctx.fillStyle = '#ef5b63'
      ctx.beginPath(); ctx.ellipse(h.x * k, e.G - (40 + h.lift) * k, 18 * k, 42 * k, 0, 0, Math.PI * 2); ctx.fill(); ctx.globalAlpha = 1
    }
    // 字幕：左上に番号と名前・その下に元の技と一段上にした所
    const font = (px: number, b = false) => `${b ? '700 ' : ''}${px * k}px "Zen Kaku Gothic New","Hiragino Sans",sans-serif`
    ctx.textAlign = 'left'; ctx.fillStyle = '#e8c27a'; ctx.font = font(14, true)
    ctx.fillText(`${o.no} ${o.name}`, 12 * k, 40 * k)
    ctx.fillStyle = '#c9ccd6'; ctx.font = font(9.5)
    ctx.fillText(`元：${o.from}`, 12 * k, 54 * k)
    ctx.fillText(`→ ${o.up}`, 12 * k, 67 * k)
    ctx.textAlign = 'right'; ctx.fillStyle = '#7c8492'; ctx.font = font(9)
    ctx.fillText('先代自身の技（輪は出ない）', w - 10 * k, 22 * k)
  }
}

function make(o: Own): MockupMove {
  const m: MockupMove = {
    id: o.id, boss: '先代の姫', label: `先代自身の技 ${o.no} ${o.name}（元：${o.from}）`, body: 'human-rapier-skirt', durMs: o.dur, scale: 0.95,
    baseX: 300, heroAt: 150, interp: 'linear', keys: bakeTracks(seq(o.keys), o.dur, build), phases: o.phases, hits: o.hits, activeMs: 60,
    box: [40, 120, -128, -88], paths: ['sF'],
    source: `先代自身の技の試し（11-05）。元にした主人公の技＝${o.from}。一段上にした所＝${o.up}。拍は仮。灯りの輪は出さない。`,
  }
  m.stage = { front: front(o, m) }
  m.hero = (ctx, t, e) => drawHero(ctx, heroAt(o.hero, t), e)
  return m
}

export const PRINCESS_OWN_MOCKUPS: MockupMove[] = PRINCESS_OWN_LIST.map(make)
